/**
 * Human review request form shown after the user accepts the offer (quota > 0).
 *
 * One required question (≤500), optional notes (≤1000). On submit it consumes
 * one entitlement and creates a pending request. Copy never promises a grade
 * change.
 *
 * @module HumanReviewRequestForm
 */
import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateReviewRequest } from '@/hooks/useHumanReviews';
import { ATTACHED_QUESTION_MAX, NOTES_MAX } from '@/lib/ratingConstants';
import type { ReviewModule } from '@/lib/humanReviewsApi';

export interface HumanReviewRequestFormProps {
  readonly targetAnswerId: string;
  readonly module: ReviewModule;
  readonly ratingId?: string;
  readonly onSubmitted?: () => void;
}

export function HumanReviewRequestForm({
  targetAnswerId,
  module,
  ratingId,
  onSubmitted,
}: HumanReviewRequestFormProps) {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const createReview = useCreateReviewRequest();

  const moduleLabel = t(`moduleSelection.modules.${module}.label`, { defaultValue: module });
  const trimmedQuestion = question.trim();
  const canSubmit = trimmedQuestion.length > 0 && !createReview.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const result = await createReview.mutateAsync({
        targetAnswerId,
        module,
        ratingId,
        attachedQuestion: trimmedQuestion,
        notes: notes.trim() || undefined,
      });
      if (result) {
        setSubmitted(true);
        onSubmitted?.();
      }
    } catch (error) {
      const status = (error as { status?: number })?.status;
      toast.error(
        status === 403 && error instanceof Error
          ? error.message
          : status === 409
          ? t('humanReview.form.quotaError')
          : t('humanReview.form.questionRequired'),
      );
    }
  };

  if (submitted) {
    return (
      <div
        className="mt-3 flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 p-4"
        role="status"
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
        <div className="space-y-1">
          <p className="font-ui text-sm font-semibold text-foreground">
            {t('humanReview.form.successTitle')}
          </p>
          <p className="font-ui text-xs text-muted-foreground">
            {t('humanReview.form.successMessage')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
      <p className="font-ui text-xs font-medium text-muted-foreground">
        {t('humanReview.form.contextLabel', { module: moduleLabel })}
      </p>

      <div className="space-y-1.5">
        <Label htmlFor={`hr-question-${targetAnswerId}`} className="font-ui text-xs font-semibold">
          {t('humanReview.form.questionLabel')}
        </Label>
        <Textarea
          id={`hr-question-${targetAnswerId}`}
          value={question}
          onChange={(e) => setQuestion(e.target.value.slice(0, ATTACHED_QUESTION_MAX))}
          placeholder={t('humanReview.form.questionPlaceholder')}
          maxLength={ATTACHED_QUESTION_MAX}
          rows={2}
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`hr-notes-${targetAnswerId}`} className="font-ui text-xs font-semibold">
          {t('humanReview.form.notesLabel')}
        </Label>
        <Textarea
          id={`hr-notes-${targetAnswerId}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
          placeholder={t('humanReview.form.notesPlaceholder')}
          maxLength={NOTES_MAX}
          rows={2}
        />
      </div>

      <Button type="button" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
        {createReview.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
        {createReview.isPending ? t('humanReview.form.submitting') : t('humanReview.form.submit')}
      </Button>
    </div>
  );
}
